import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import OffersModuleLayout from "./OffersModuleLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { API_URL } from "../config/api";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  MapPin,
  Users,
  Ship,
  Plane,
  Truck,
  Train,
  Package,
  ArrowRight,
  RefreshCw,
  Calendar,
  ChevronRight
} from "lucide-react";


const SERVICE_ICONS = {
  fcl: Ship,
  lcl: Ship,
  air: Plane,
  inland: Truck,
  rail: Train,
  rail_truck: Train
};

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
  accepted: "bg-green-100 text-green-700",
  confirmed: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700"
};

export default function OffersDashboardPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");

  // Determine active tab based on current path
  const activeTab = location.pathname.includes("/pricing") ? "pricing" 
    : location.pathname.includes("/rate-cards") ? "rate-cards" 
    : "dashboard";

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/offers/analytics?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      // Don't show error toast for empty data - just use defaults
      console.error("Analytics fetch:", error);
      setAnalytics({ summary: {} });
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-EU').format(value || 0);
  };

  const summary = analytics?.summary || {};

  return (
    <OffersModuleLayout>
      {/* Dashboard Content */}
      <div className="max-w-[1800px] mx-auto p-6 space-y-6">
        {/* Period Selector & Refresh */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Performance Overview</h2>
            <p className="text-sm text-muted-foreground">Track your quotation analytics</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-slate-100 rounded-lg p-1">
              {[
                { value: "7d", label: "7 Days" },
                { value: "30d", label: "30 Days" },
                { value: "90d", label: "90 Days" },
                { value: "all", label: "All Time" }
              ].map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    period === p.value 
                      ? "bg-white text-slate-900 shadow-sm" 
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {loading && !analytics ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading dashboard...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <FileText className="h-8 w-8 text-slate-400" />
                <Badge variant="outline" className="text-slate-600">Total</Badge>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-bold text-slate-900">{formatNumber(summary.total_offers)}</p>
                <p className="text-xs text-slate-500">Total Offers</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Send className="h-8 w-8 text-blue-400" />
                <Badge className="bg-blue-500">Sent</Badge>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-bold text-blue-900">{formatNumber(summary.sent)}</p>
                <p className="text-xs text-blue-600">{formatCurrency(summary.sent_value)}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Clock className="h-8 w-8 text-amber-400" />
                <Badge className="bg-amber-500">Pending</Badge>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-bold text-amber-900">{formatNumber(summary.pending)}</p>
                <p className="text-xs text-amber-600">Awaiting Response</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="h-8 w-8 text-green-400" />
                <Badge className="bg-green-500">Won</Badge>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-bold text-green-900">{formatNumber(summary.accepted)}</p>
                <p className="text-xs text-green-600">{formatCurrency(summary.accepted_value)}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <XCircle className="h-8 w-8 text-red-400" />
                <Badge className="bg-red-500">Lost</Badge>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-bold text-red-900">{formatNumber(summary.rejected)}</p>
                <p className="text-xs text-red-600">Rejected</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <TrendingUp className="h-8 w-8 text-indigo-400" />
                <Badge className="bg-indigo-500">Rate</Badge>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-bold text-indigo-900">{summary.conversion_rate}%</p>
                <p className="text-xs text-indigo-600">Conversion Rate</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Services Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Services Breakdown
              </CardTitle>
              <CardDescription>Offers by service type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics?.services_breakdown?.map((service, idx) => {
                  const Icon = SERVICE_ICONS[service.service] || Package;
                  const maxCount = Math.max(...(analytics?.services_breakdown?.map(s => s.count) || [1]));
                  const percentage = (service.count / maxCount) * 100;
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-24 flex items-center gap-2">
                        <Icon className="h-4 w-4 text-slate-500" />
                        <span className="text-sm font-medium uppercase">{service.service || 'N/A'}</span>
                      </div>
                      <div className="flex-1">
                        <div className="h-8 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div 
                            className="h-full bg-gradient-to-r from-primary to-blue-400 rounded-full flex items-center justify-end pr-3"
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 0.5, delay: idx * 0.1 }}
                          >
                            <span className="text-xs font-bold text-white">{service.count}</span>
                          </motion.div>
                        </div>
                      </div>
                      <div className="w-28 text-right">
                        <span className="text-sm font-semibold text-green-600">{formatCurrency(service.value)}</span>
                      </div>
                    </div>
                  );
                })}
                {(!analytics?.services_breakdown || analytics.services_breakdown.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No service data available</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Routes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Top Routes
              </CardTitle>
              <CardDescription>Most quoted trade lanes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics?.top_routes?.slice(0, 8).map((route, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-lg font-bold text-slate-400 w-6">#{idx + 1}</span>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate max-w-[150px]" title={route.origin}>
                          {route.origin}
                        </span>
                        <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm font-medium truncate max-w-[150px]" title={route.destination}>
                          {route.destination}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <Badge variant="outline">{route.count} offers</Badge>
                      <span className="text-sm font-semibold text-green-600 w-24 text-right">
                        {formatCurrency(route.avg_value)} avg
                      </span>
                    </div>
                  </motion.div>
                ))}
                {(!analytics?.top_routes || analytics.top_routes.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No route data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Total Value Card */}
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <DollarSign className="h-10 w-10 text-slate-400" />
                <Badge className="bg-white/20 text-white">Total Pipeline</Badge>
              </div>
              <p className="text-4xl font-bold mb-2">{formatCurrency(summary.total_value)}</p>
              <p className="text-slate-400">Combined value of all offers</p>
              <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Won Value</p>
                  <p className="text-lg font-semibold text-green-400">{formatCurrency(summary.accepted_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Pending Value</p>
                  <p className="text-lg font-semibold text-amber-400">{formatCurrency(summary.sent_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Customers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Top Customers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics?.top_customers?.slice(0, 5).map((customer, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium truncate max-w-[140px]" title={customer.customer}>
                        {customer.customer}
                      </span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-green-600">{formatCurrency(customer.value)}</p>
                      <p className="text-xs text-muted-foreground">{customer.count} offers</p>
                    </div>
                  </div>
                ))}
                {(!analytics?.top_customers || analytics.top_customers.length === 0) && (
                  <p className="text-center text-muted-foreground py-4">No customer data</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rejection Reasons */}
          {analytics?.rejection_reasons?.length > 0 && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <XCircle className="h-5 w-5" />
                  Rejection Reasons
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics.rejection_reasons.map((reason, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2">
                      <span className="text-sm text-slate-600 truncate max-w-[180px]" title={reason.reason}>
                        {reason.reason}
                      </span>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        {reason.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Recent Offers */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Recent Offers
              </CardTitle>
              <CardDescription>Latest quotations sent to customers</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/offers/pricing'}>
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 text-xs font-medium text-slate-500">Offer #</th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-slate-500">Customer</th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-slate-500">Route</th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-slate-500">Service</th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-slate-500">Value</th>
                  <th className="text-center py-3 px-2 text-xs font-medium text-slate-500">Status</th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-slate-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.recent_offers?.map((offer, idx) => {
                  const Icon = SERVICE_ICONS[offer.service_type] || Package;
                  return (
                    <motion.tr 
                      key={offer.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      className="border-b last:border-0 hover:bg-slate-50"
                    >
                      <td className="py-3 px-2">
                        <span className="font-mono text-sm font-medium text-primary">{offer.offer_number}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-sm truncate max-w-[150px] block" title={offer.customer_company}>
                          {offer.customer_company || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1 text-sm">
                          <span className="truncate max-w-[80px]">{offer.origin?.split(',')[0] || 'N/A'}</span>
                          <ArrowRight className="h-3 w-3 text-slate-400 flex-shrink-0" />
                          <span className="truncate max-w-[80px]">{offer.destination?.split(',')[0] || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          <Icon className="h-4 w-4 text-slate-400" />
                          <span className="text-xs uppercase">{offer.service_type}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-semibold text-green-600">{formatCurrency(offer.subtotal)}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Badge className={STATUS_COLORS[offer.status] || "bg-slate-100"}>
                          {offer.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-xs text-muted-foreground">
                          {offer.created_at ? new Date(offer.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
            {(!analytics?.recent_offers || analytics.recent_offers.length === 0) && (
              <p className="text-center text-muted-foreground py-8">No recent offers</p>
            )}
          </div>
        </CardContent>
      </Card>
          </>
        )}
      </div>
    </OffersModuleLayout>
  );
}
